import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import DashboardNavBar from './DashboardNavBar';
import { gTier } from '../../utils/gamificationLabel';

// ─── helpers ────────────────────────────────────────────────────────────────

function getAuthToken() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') ||
    null
  );
}

function StarRating({ rating }) {
  if (!rating) return null;
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span style={{ fontSize: 14, color: '#f59e0b', letterSpacing: 1 }}>
      {'★'.repeat(full)}
      {half ? '½' : ''}
      {'☆'.repeat(empty)}
    </span>
  );
}

// ─── role badge ──────────────────────────────────────────────────────────────

const ROLE_STYLES = {
  owner:   { color: '#166534', background: '#dcfce7' },
  manager: { color: '#1e40af', background: '#dbeafe' },
  staff:   { color: '#374151', background: '#f3f4f6' },
};

function RoleBadge({ role }) {
  if (!role) return null;
  const styles = ROLE_STYLES[role] || ROLE_STYLES.staff;
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: styles.color,
      background: styles.background,
      flexShrink: 0,
    }}>
      {role}
    </span>
  );
}

// ─── spinner ─────────────────────────────────────────────────────────────────

function Spinner({ size = 40, color = '#f97316' }) {
  return (
    <>
      <div style={{
        width: size,
        height: size,
        border: `4px solid rgba(0,0,0,0.08)`,
        borderTop: `4px solid ${color}`,
        borderRadius: '50%',
        animation: 'hl-spin 0.9s linear infinite',
        margin: '0 auto',
      }} />
      <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
    </>
  );
}

// ─── tile ────────────────────────────────────────────────────────────────────

function Tile({ emoji, title, subtitle, accentColor, onClick, locked = false, comingSoon = false }) {
  const [hovered, setHovered] = useState(false);

  const tileStyle = {
    position: 'relative',
    background: locked ? '#f9fafb' : '#ffffff',
    borderRadius: 16,
    boxShadow: hovered && !locked
      ? '0 8px 24px rgba(0,0,0,0.14)'
      : '0 2px 8px rgba(0,0,0,0.08)',
    padding: '20px 12px 18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    cursor: locked ? 'default' : 'pointer',
    opacity: locked ? 0.6 : 1,
    transform: hovered && !locked ? 'scale(1.025)' : 'scale(1)',
    transition: 'box-shadow 0.18s ease, transform 0.18s ease',
    borderTop: `4px solid ${accentColor}`,
    minHeight: 160,
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
  };

  return (
    <div
      style={tileStyle}
      onClick={locked ? undefined : onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onTouchStart={() => setHovered(true)}
      onTouchEnd={() => setHovered(false)}
    >
      <div style={{ fontSize: 32, lineHeight: 1, marginBottom: 10 }}>{emoji}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 5 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.35 }}>{subtitle}</div>
      {comingSoon && <ComingSoonBadge />}
    </div>
  );
}

// ─── coming soon badge ───────────────────────────────────────────────────────

function ComingSoonBadge() {
  const { t } = useTranslation();
  return (
    <div style={{
      marginTop: 8,
      padding: '2px 8px',
      background: '#f3f4f6',
      borderRadius: 10,
      fontSize: 10,
      fontWeight: 600,
      color: '#9ca3af',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    }}>
      {t('vd.comingSoon')}
    </div>
  );
}

// ─── stat chip ───────────────────────────────────────────────────────────────

function StatChip({ label, value }) {
  return (
    <div style={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      padding: '8px 18px',
      flexShrink: 0,
    }}>
      <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{value}</span>
      <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{label}</span>
    </div>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

const VenueDashboardHomePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [state, setState] = useState('loading'); // loading | ready | error
  const [errorType, setErrorType] = useState(null); // 401 | 403 | 404 | generic
  const [venue, setVenue] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [staff, setStaff] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [venueScore, setVenueScore] = useState(null); // EV-603 P5 — venue gamification score

  const fetchDashboard = useCallback(async () => {
    setState('loading');
    setErrorType(null);

    const token = getAuthToken();
    if (!token) {
      setState('error');
      setErrorType(401);
      return;
    }

    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        setState('error');
        setErrorType(401);
        return;
      }
      if (res.status === 403) {
        setState('error');
        setErrorType(403);
        return;
      }
      if (res.status === 404) {
        setState('error');
        setErrorType(404);
        return;
      }

      const data = await res.json();

      if (!data.success) {
        setState('error');
        setErrorType('generic');
        return;
      }

      setVenue(data.venue || null);
      setMyRole(data.myRole || null);
      setStaff(data.staff || []);
      setPhotos(data.photos || []);
      setPendingInvites(data.pendingInvites || []);

      // 403-equivalent: fetch succeeded but caller has no role here
      if (!data.myRole) {
        setState('error');
        setErrorType(403);
        return;
      }

      setState('ready');

      // EV-603 P5 — load venue gamification score (non-blocking, best-effort)
      if (data.venue?.id) {
        const scoreBase = getApiBaseUrl();
        fetch(`${scoreBase}/me/venues/${data.venue.id}/score`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((j) => { if (j?.success) setVenueScore(j); })
          .catch(() => {}); // silent — score card is supplemental
      }
    } catch (err) {
      console.error('[VenueDashboardHomePage] fetch error:', err);
      setState('error');
      setErrorType('generic');
    }
  }, [slug]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const go = (path) => navigate(`/venue-dashboard/${slug}${path}`);
  const canManageStaff = myRole === 'owner' || myRole === 'manager';

  // ── loading ────────────────────────────────────────────────────────────────
  if (state === 'loading') {
    return (
      <div style={styles.screen}>
        {/* orange header still visible during load */}
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={() => navigate('/venue-dashboard')}>←</button>
          <span style={styles.headerTitle}>{t('vd.loading')}</span>
          <div style={{ width: 40 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <Spinner />
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>{t('vd.loadingVenue')}</p>
        </div>
      </div>
    );
  }

  // ── error ──────────────────────────────────────────────────────────────────
  if (state === 'error') {
    let icon = '⚠️';
    let heading = t('vd.somethingWentWrong');
    let sub = t('vd.pleaseTryAgainLater');

    if (errorType === 401) {
      icon = '🔒';
      heading = t('vd.pleaseLogIn');
      sub = t('vd.needLoggedInToViewPage');
    } else if (errorType === 403) {
      icon = '🚫';
      heading = t('vd.accessDenied');
      sub = t('vd.noAccessToThisVenue');
    } else if (errorType === 404) {
      icon = '🔍';
      heading = t('vd.venueNotFound');
      sub = t('vd.venueNotExistOrWrongLink');
    }

    return (
      <div style={styles.screen}>
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={() => navigate('/venue-dashboard')}>←</button>
          <span style={styles.headerTitle}>{t('vd.title')}</span>
          <div style={{ width: 40 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center', gap: 12 }}>
          <div style={{ fontSize: 48 }}>{icon}</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>{heading}</h2>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0, maxWidth: 280, lineHeight: 1.5 }}>{sub}</p>
          {errorType === 401 && (
            <button
              style={styles.primaryBtn}
              onClick={() => navigate('/login')}
            >
              {t('vd.logIn')}
            </button>
          )}
          <button
            style={{ ...styles.primaryBtn, background: '#6b7280', marginTop: errorType === 401 ? 0 : 8 }}
            onClick={() => navigate('/venue-dashboard')}
          >
            {t('vd.myVenues')}
          </button>
        </div>
      </div>
    );
  }

  // ── ready ──────────────────────────────────────────────────────────────────
  return (
    <div style={styles.screen}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={styles.header}>
        <button
          style={styles.backBtn}
          onClick={() => navigate('/venue-dashboard')}
          aria-label={t('vd.backToMyVenues')}
        >
          ←
        </button>
        <span style={styles.headerTitle} title={venue?.name}>
          {venue?.name || t('vd.title')}
        </span>
        <div style={{ width: 40, display: 'flex', justifyContent: 'flex-end' }}>
          <RoleBadge role={myRole} />
        </div>
      </div>

      {/* ── Scrollable body ────────────────────────────────────────────────── */}
      <div style={styles.body}>

        {/* ── Venue info strip ───────────────────────────────────────────── */}
        <div style={styles.infoCard}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
            {venue?.name}
          </div>
          {(venue?.city || venue?.venueType) && (
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
              {[venue.city, venue.venueType].filter(Boolean).join(' · ')}
            </div>
          )}
          {venue?.googleRating && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <StarRating rating={venue.googleRating} />
              <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>{parseFloat(venue.googleRating).toFixed(1)}</span>
              {venue.googleReviewCount ? (
                <span style={{ fontSize: 12, color: '#9ca3af' }}>{t('vd.reviewsCountParen', { formatted: venue.googleReviewCount.toLocaleString() })}</span>
              ) : null}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
            {venue?.phone && (
              <a
                href={`tel:${venue.phone}`}
                style={styles.contactLink}
              >
                📞 {venue.phone}
              </a>
            )}
            {venue?.website && (
              <a
                href={venue.website.startsWith('http') ? venue.website : `https://${venue.website}`}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.contactLink}
              >
                🌐 {t('vd.website')}
              </a>
            )}
            {venue?.address && (
              <span style={{ fontSize: 12, color: '#9ca3af', alignSelf: 'center' }}>
                📍 {venue.address}
              </span>
            )}
          </div>
          {/* View public page link */}
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
            <a
              href={`/venue/${slug}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 12, color: '#f97316', fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              🌐 {t('vd.viewPublicProfile')}
            </a>
          </div>
        </div>

        {/* EV-603 P5 — venue gamification score card (shown when data is available) */}
        {venueScore && (
          <VenueScoreCard score={venueScore} />
        )}

        {/* ── Tile grid ──────────────────────────────────────────────────── */}
        <div style={styles.grid}>

          {/* 1. Photos */}
          <Tile
            emoji="📸"
            title={t('vd.photos')}
            subtitle={photos.length > 0 ? t('vd.photosCount', { count: photos.length }) : t('vd.managePhotos')}
            accentColor="#f59e0b"
            onClick={() => go('/photos')}
          />

          {/* 2. Info & Vitals */}
          <Tile
            emoji="ℹ️"
            title={t('vd.infoVitals')}
            subtitle={t('vd.editPublicInfo')}
            accentColor="#3b82f6"
            onClick={() => go('/info')}
          />

          {/* 3. Staff & Invites — locked for plain staff */}
          {canManageStaff ? (
            <Tile
              emoji="👥"
              title={t('vd.staffInvites')}
              subtitle={staff.length > 0 ? t('vd.staffCount', { count: staff.length }) : t('vd.manageTeam')}
              accentColor="#8b5cf6"
              onClick={() => go('/staff')}
            />
          ) : (
            <Tile
              emoji="🔒"
              title={t('vd.staffInvites')}
              subtitle={t('vd.managerAccessRequired')}
              accentColor="#8b5cf6"
              locked
            />
          )}

          {/* 4. WiFi Lab */}
          <Tile
            emoji="📶"
            title={t('vd.wifiLab')}
            subtitle={t('vd.speedTestsZones')}
            accentColor="#10b981"
            onClick={() => go('/wifi')}
            comingSoon
          />

          {/* 5. Menu & Prices — EV-759 live editor */}
          <Tile
            emoji="🍽️"
            title={t('vd.menuPrices')}
            subtitle={t('vd.itemsServicesRates')}
            accentColor="#ef4444"
            onClick={() => go('/menu')}
          />

          {/* 5b. Order History — EV-768 (owner/manager) */}
          {canManageStaff ? (
            <Tile
              emoji="🧾"
              title={t('vd.orderHistory', 'Order History')}
              subtitle={t('vd.whoOrderedWhat', 'Who ordered what')}
              accentColor="#3b82f6"
              onClick={() => go('/orders')}
            />
          ) : (
            <Tile
              emoji="🧾"
              title={t('vd.orderHistory', 'Order History')}
              subtitle={t('vd.managerAccessRequired')}
              accentColor="#3b82f6"
              locked
            />
          )}

          {/* EV-435 — Service orders (laundry/gym/…) + price-list editor */}
          <Tile
            emoji="🧺"
            title={t('vd.serviceOrders')}
            subtitle={t('vd.laundryServiceTickets')}
            accentColor="#0ea5e9"
            onClick={() => go('/service-orders')}
          />
          {canManageStaff && (
            <Tile
              emoji="🏷️"
              title={t('vd.servicePrices')}
              subtitle={t('vd.editYourPriceList')}
              accentColor="#8b5cf6"
              onClick={() => go('/service-prices')}
            />
          )}

          {/* 6. Offers & Promos */}
          <Tile
            emoji="🎁"
            title={t('vd.offersPromos')}
            subtitle={t('vd.dealsPromotions')}
            accentColor="#f97316"
            onClick={() => go('/offers')}
            comingSoon
          />

          {/* 6a. Hangouts — VEN-1449 sponsor meetups at your venue */}
          <Tile
            emoji="🤝"
            title={t('vd.hangouts')}
            subtitle={t('vd.sponsorMeetupsAtYourVenue')}
            accentColor="#E46E34"
            onClick={() => go('/hangouts')}
          />

          {/* 6b. Loyalty — CON-525 Collect the Lees */}
          <Tile
            emoji="🦊"
            title={t('vd.loyalty')}
            subtitle={t('vd.collectTheLees')}
            accentColor="#E46E34"
            onClick={() => go('/loyalty')}
          />

          {/* 7. Events */}
          <Tile
            emoji="🎉"
            title={t('vd.events')}
            subtitle={t('vd.scheduleBookings')}
            accentColor="#6366f1"
            onClick={() => go('/events')}
            comingSoon
          />

          {/* 8. Reviews Console */}
          <Tile
            emoji="⭐"
            title={t('vd.reviewsConsole')}
            subtitle={t('vd.ratingsFeedback')}
            accentColor="#f59e0b"
            onClick={() => go('/reviews')}
            comingSoon
          />

          {/* 9. Vitals & Payments — owner only */}
          {myRole === 'owner' ? (
            <Tile
              emoji="💳"
              title={t('vd.vitalsPayments')}
              subtitle={t('vd.analyticsBilling')}
              accentColor="#0ea5e9"
              onClick={() => go('/vitals')}
              comingSoon
            />
          ) : (
            <Tile
              emoji="💳"
              title={t('vd.vitalsPayments')}
              subtitle={t('vd.ownerAccessRequired')}
              accentColor="#0ea5e9"
              locked
            />
          )}

          {/* 10. Staff QR */}
          {canManageStaff ? (
            <Tile
              emoji="🪪"
              title={t('vd.staffQR')}
              subtitle={t('vd.quickOnboardingQR')}
              accentColor="#8b5cf6"
              onClick={() => go('/staff-qr')}
              comingSoon
            />
          ) : (
            <Tile
              emoji="🪪"
              title={t('vd.staffQR')}
              subtitle={t('vd.managerAccessRequired')}
              accentColor="#8b5cf6"
              locked
            />
          )}

          {/* 11. Redemption Centre */}
          {myRole === 'owner' ? (
            <Tile
              emoji="🏆"
              title={t('vd.redemptionCentre')}
              subtitle={t('vd.loyaltyRewards')}
              accentColor="#10b981"
              onClick={() => go('/redemption')}
              comingSoon
            />
          ) : (
            <Tile
              emoji="🏆"
              title={t('vd.redemptionCentre')}
              subtitle={t('vd.ownerAccessRequired')}
              accentColor="#10b981"
              locked
            />
          )}

          {/* 12. Payments — owner/manager only (VietQR) */}
          {(myRole === 'owner' || myRole === 'manager') ? (
            <Tile
              emoji="💰"
              title={t('vd.payments')}
              subtitle={t('vd.vietQrPayTheBill')}
              accentColor="#3b82f6"
              onClick={() => go('/payments')}
            />
          ) : (
            <Tile
              emoji="💰"
              title={t('vd.payments')}
              subtitle={t('vd.managerAccessRequired')}
              accentColor="#3b82f6"
              locked
            />
          )}

        </div>

        {/* ── Quick stats bar (owner / manager only) ─────────────────────── */}
        {canManageStaff && (
          <div style={styles.statsBarWrapper}>
            <div style={styles.statsBar}>
              <StatChip label={t('vd.staff')} value={staff.length} />
              <StatChip label={t('vd.pendingInvites')} value={pendingInvites.length} />
              <StatChip label={t('vd.photos')} value={photos.length} />
            </div>
          </div>
        )}

        {/* bottom breathing room */}
        <div style={{ height: 40 }} />
      </div>
      <DashboardNavBar />
    </div>
  );
};

// ─── shared style objects ─────────────────────────────────────────────────────

const styles = {
  screen: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f9fafb',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    height: 64,
    background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: 'white',
    fontSize: 22,
    fontWeight: 700,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 8,
    lineHeight: 1,
    width: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    color: 'white',
    fontWeight: 700,
    fontSize: 16,
    flex: 1,
    textAlign: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    padding: '0 8px',
  },
  body: {
    flex: 1,
    padding: '16px 16px 0',
    overflowY: 'auto',
  },
  infoCard: {
    background: '#ffffff',
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
  },
  contactLink: {
    fontSize: 12,
    color: '#f97316',
    textDecoration: 'none',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginBottom: 16,
  },
  statsBarWrapper: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    marginBottom: 8,
  },
  statsBar: {
    display: 'flex',
    gap: 10,
    paddingBottom: 4,
    minWidth: 'min-content',
  },
  primaryBtn: {
    display: 'block',
    width: '100%',
    maxWidth: 240,
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 8,
    boxShadow: '0 4px 12px rgba(249,115,22,0.3)',
  },
};

// ─── EV-603 P5 — Venue Score card ────────────────────────────────────────────
// Compact card showing the venue's gamification tier, total points, QR entries
// this month, and progress to the next tier. Placed near the top of the page
// (above the tile grid) so owners/managers see it immediately.

function VenueScoreCard({ score }) {
  const { t } = useTranslation();
  const { total_points, tier, next_tier, points_to_next, qr_stats } = score;
  const entries30d = qr_stats?.entries_30d ?? 0;
  const pct = next_tier
    ? Math.round(Math.min(1, 1 - points_to_next / next_tier.min_points) * 100)
    : 100;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #fff7ed, #fef3c7)',
      border: '1px solid #fde68a',
      borderRadius: 14,
      padding: '14px 16px',
      marginBottom: 12,
      boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
    }}>
      {/* header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ fontSize: 26, lineHeight: 1 }}>{tier?.icon || '🏅'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#78350f' }}>
            {t('vd.venueScore')} · {tier ? gTier(tier.slug, tier.label) : t('vd.starter')}
          </div>
          <div style={{ fontSize: 12, color: '#b45309' }}>
            {total_points.toLocaleString()} ⭐
            {next_tier ? ` · ${t('vd.toNextTier', { count: points_to_next.toLocaleString(), label: gTier(next_tier.slug, next_tier.label), icon: next_tier.icon })}` : ` · ${t('vd.maxTierReached')}`}
          </div>
        </div>
        {/* QR entries stat */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#78350f' }}>{entries30d}</div>
          <div style={{ fontSize: 10, color: '#b45309', fontWeight: 600 }}>{t('vd.appEntries')}<br />{t('vd.thisMonth')}</div>
        </div>
      </div>
      {/* progress bar */}
      {next_tier && (
        <div style={{ height: 6, background: '#fde68a', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: 'linear-gradient(90deg, #E46E34, #f59e0b)',
            borderRadius: 4,
            transition: 'width 0.6s ease',
          }} />
        </div>
      )}
    </div>
  );
}

export default VenueDashboardHomePage;
